/**
 * The admin dashboard is filled by the installed modules through two well-known slots:
 *
 * - `dashboard.tiles`: KPI tiles (`<KpiTile …/>`), e.g. vendors, products, and later sales and orders;
 * - `dashboard.tasks`: "Needs attention" rows (`<DashboardTask …/>`), e.g. applications or products to review.
 *
 * Each entry fetches its own numbers and carries its own permission, so the dashboard shows only what the user may
 * see and what is installed.
 */

import { Link } from "react-router";

import { Badge, Icon, type IconName } from "@motolynk/ui";

export const DASHBOARD_TILES = "dashboard.tiles";
export const DASHBOARD_TASKS = "dashboard.tasks";

export interface DashboardTaskProps {
  to: string;
  label: string;
  count: number | undefined;
  icon?: IconName;
  tone?: "brand" | "warning" | "danger";
}

/** One "needs attention" row; nothing when the count is 0 (or still loading). */
export function DashboardTask({
  to,
  label,
  count,
  icon = "alert",
  tone = "brand",
}: DashboardTaskProps) {
  if (!count) return null;
  return (
    <li>
      <Link
        to={to}
        className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-sm hover:bg-foreground/5"
      >
        <span className="flex min-w-0 items-center gap-2">
          <Icon name={icon} size={16} className="shrink-0 text-muted-foreground" />
          <span className="truncate">{label}</span>
        </span>
        <Badge tone={tone}>{count}</Badge>
      </Link>
    </li>
  );
}
